/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useMemo, useState} from "react";
import {MRT_ColumnDef, MRT_Row} from "material-react-table";
import {Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Radio, TextField} from "@mui/material";
import {fetchImageUrl, useGenerateURL, useImagePostRequest, usePostRequest} from "../../../../api/helper";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {addRealWorldObjectImageEndpoint} from "../../objectApp/constants";
import {BASE_REAL_WORLD_OBJECTS_ENDPOINT} from "../constants";
import {getDateTime} from "../../../../helpers/helpers";
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";


/**
 * Image Schema in the database
 */
interface Image {
  type: string;
  fileName: string;
  storageLocation: string;
  comments: string;
  lastUpdated: string;
  latestVersion: string;
  isMainTableThumbnail: boolean;
  tempFile?: File;
}

/**
 * Image Data for Table
 */
interface ImageTableData extends Image {
  image: string;
  actions: string;
}

/**
 * `ImageProperties` manages and displayes thumbnails in the RWO app within a collapsible tab interface.
 * It provides functionalities for adding, editing, and deleting
 * images, as well as setting one image to be displayed in the main table.
 *
 * @param {any} props - The properties passed to the component.
 * @param {Function} props.onDataUpdate - Callback function to handle data updates.
 * @param {Function} props.onImageUpdate - Callback function to handle image updates.
 *
 * @returns {JSX.Element} The rendered `ImageProperties` component.
 */
const ImageProperties = (props: any) => {
  /************************************************************************************************
   * Component State
   ************************************************************************************************/
  const [rowsData, setRowsData] = useState<ImageTableData[]>([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  const [imageUrls, setImageUrls] = useState<{ [key: string]: string | null }>({});
  const [imageUrl, setImageUrl] = useState("");

  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [newImage, setNewImage] = useState<string | null>(null);

  const generateURL = useGenerateURL();
  const uploadImageHandle = useImagePostRequest(addRealWorldObjectImageEndpoint);
  const postRWOUpdate = usePostRequest(BASE_REAL_WORLD_OBJECTS_ENDPOINT)

  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  const [imageKey, setImageKey] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRowIndex, setDeleteRowIndex] = useState<number | null>(null);

  const [editRowIndex, setEditRowIndex] = useState<number | null>(null);
  const [editableCell, setEditableCell] = useState<{ rowIndex: number; columnId: string } | null>(null);
  const {onDataUpdate} = props;
  const {onImageUpdate} = props;
  const [pendingImages, setPendingImages] = useState<FormData[]>([]);  // State to accumulate pending formData
  const [newFileName, setNewFileName] = useState("");

  /************************************************************************************************
   * Handle Data Updates
   ************************************************************************************************/
  useEffect(() => {
    if (onDataUpdate) {
      if (isDeleteMode === false) {
        onDataUpdate({images: rowsData, pendingImages, "isDeleteMode": isDeleteMode});
      } else {
        onDataUpdate({images: rowsData, pendingImages, "isDeleteMode": {isDeleteMode, imageKey}});
      }
    }
  }, [rowsData, pendingImages, onDataUpdate, isDeleteMode]);

  useEffect(() => {
    if (props.object && props.object.images) {
      const images: Image[] = Object.values(props.object.images) as Image[];
      const data = convertImgSchemaToImgData(images);
      setRowsData(data);
    } else {
      setRowsData([]);
    }
  }, [props.object]);

  useEffect(() => {
    if (rowsData.length > 0) {
      const fetchAllImages = async () => {
        const urls = await Promise.all(rowsData.map(row => fetchImageUrl(row.fileName, generateURL)));
        const urlsMap = rowsData.reduce((acc, row, index) => {
          acc[row.fileName] = urls[index];
          return acc;
        }, {} as { [key: string]: string | null });
        setImageUrls(urlsMap);
      };
      fetchAllImages();
    }
  }, [rowsData]);

  // Effect hook to run when pendingImages changes
  useEffect(() => {
    if (pendingImages.length > 0) {
      onImageUpdate(pendingImages);
    }
  }, [pendingImages]);

  /************************************************************************************************
   * Helper Functions
   ************************************************************************************************/

  /**
   * Handles the click event on a table cell, setting the cell to be editable.
   *
   * @param rowIndex - The index of the row containing the clicked cell.
   * @param columnId - The ID of the column containing the clicked cell.
   */
  const handleCellClick = (rowIndex: number, columnId: string) => {
    setEditableCell({rowIndex, columnId});
  };

  /**
   * Handles the change event for a cell in a table.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the input element.
   * @param {number} rowIndex - The index of the row being updated.
   * @param {string} columnId - The ID of the column being updated.
   */
  const handleCellChange = (e: React.ChangeEvent<HTMLInputElement>, rowIndex: number, columnId: string) => {
    const {value} = e.target;
    setRowsData((prevRowsData) => {
        const _newRowData = prevRowsData.map((row, index) =>
          index === rowIndex ? {...row, [columnId]: value} : row
        )
        return _newRowData;
      }
    );
  };

  /**
   * Sets the `isMainTableThumbnail` property to `true` for the row at the specified index,
   * and `false` for all other rows, to restrict only one image to be displayed in the PMO table.
   *
   * @param {number} selectedRowIndex - Row index of image to be displayed.
   */
  const handleThumbnailSelection = (selectedRowIndex: number) => {
    setRowsData((prevRowsData) => {
      return prevRowsData.map((row, index) => {
        const isSelectedRow = index === selectedRowIndex;
        return {
          ...row,
          isMainTableThumbnail: isSelectedRow
        };
      });
    });
  };

  /**
   * Converts an array of Image objects to an array of ImageTableData objects.
   *
   * @param images - An array of Image objects to be converted.
   * @returns An array of ImageTableData objects.
   */
  const convertImgSchemaToImgData = (images: Image[]): ImageTableData[] => {
    const data: ImageTableData[] = images.map(image => ({
      image: image.fileName,
      type: image.type || "",
      fileName: image.fileName || "",
      storageLocation: image.storageLocation || "",
      comments: image.comments || "",
      lastUpdated: image.lastUpdated || "",
      latestVersion: image.latestVersion || "",
      actions: "",
      isMainTableThumbnail: image.isMainTableThumbnail
    }));
    return data
  }

  /**
   * Formats a given timestamp string into a readable date and time string.
   *
   * @param {string} timestamp - String to format.
   * @returns {string} - The formatted date and time string, or an empty string if the input timestamp is invalid.
   */
  const formatTimestamp = (timestamp: string) => {
    // to avoid invalid date showing in cases where no date was given and is just ""
    if (!timestamp) {
      return "";
    }
    const date = new Date(timestamp);
    return date.toLocaleString(); // Formats to readable date and time
  };

  /**
   * Defines the columns for the Images table. Refreshes upon object data or version change.
   *
   * @props
   * - `props.object`: The object containing the image data. Shows "N/A" if none.
   * - `props.isLatestVersion`: Boolean indicating if the current version is the latest.
   */
  const columns: MRT_ColumnDef<ImageTableData>[] = useMemo(() => {
    const defaultCols = [
      {
        accessorKey: "fileName",
        header: "Image Thumbnail",
        size: 220,
        Cell: ({row}: { row: MRT_Row<any> }) => {
          const [imageUrl, setImageUrl] = React.useState<string | null>(null);
          const [loading, setLoading] = React.useState<boolean>(true);
          const [error, setError] = React.useState<boolean>(false);
          const [imageValid, setImageValid] = React.useState<boolean>(true); // New state for image validity

          React.useEffect(() => {
            let isMounted = true; // Prevents state updates on unmounted components

            const loadImage = async () => {
              setLoading(true);
              setError(false);
              setImageValid(true); // Assume image is valid until proven otherwise

              try {

                if (row.original.tempFile && row.original.tempFile.name && row.original.tempFile.size) {
                  //  if the image is a temp file, use it directly
                  //  this is used to show the image in the table before it is uploaded
                  setImageUrl(URL.createObjectURL(row.original.tempFile));
                } else {
                  const url = await fetchImageUrl(row.original.fileName, generateURL);

                  if (isMounted) {
                    if (url && url.startsWith("http")) {
                      // image set successfully
                      setImageUrl(url);
                    } else {
                      console.warn(`⚠️ Invalid URL received for: ${row.original.fileName}, flagging as error.`);
                      setError(true);
                      setImageUrl(null);
                    }
                  }
                }
              } catch (err) {
                console.error(`Error fetching image for ${row.original.fileName}:`, err);
                if (isMounted) {
                  setError(true);
                  setImageUrl(null);
                }
              } finally {
                if (isMounted) setLoading(false);
              }
            };

            loadImage();

            return () => {
              isMounted = false; // Cleanup to prevent memory leaks
            };
          }, [row.original.fileName, generateURL]);

          // Function to handle broken image
          const handleImageError = () => {
            console.warn(`Image failed to load for: ${row.original.fileName}, marking as error.`);
            setImageValid(false);
            setError(true); // Ensure error is set when the image is invalid
          };

          return (
            <>
              {loading ? (
                <p>Loading...</p>
              ) : error || !imageValid ? (
                <p>N/A</p>
              ) : (
                <img
                  src={imageUrl ?? undefined}
                  alt={row.original.fileName}
                  style={{width: 100, height: 75, cursor: "pointer"}}
                  onClick={() => setSelectedImageUrl(imageUrl || "")}
                  onError={handleImageError} // Catch broken images here
                />
              )}
            </>
          );
        },
      },
      {
        accessorKey: "type",
        header: "Type",
      },
      {
        id: "fileNameURL",
        accessorKey: 'fileName',
        header: "File Name",
      },
      {
        accessorKey: "comments",
        header: "Comments",
        Cell: ({cell, row}: any) => {
          const cellValue = cell.getValue() ?? ""; // Use empty string if value is null or undefined
          return editableCell?.rowIndex === row.index && editableCell?.columnId === "comments" ? (
            <TextField
              value={cellValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCellChange(e, row.index, "comments")}
              onBlur={() => setEditableCell(null)}
              autoFocus
            />
          ) : (
            <div
              onClick={() => handleCellClick(row.index, "comments")}
              style={{cursor: "pointer", minWidth: "40px", padding: "4px"}} // Ensure clickable area is visible
            >
              {cellValue || " "} {/* Show a single space if cellValue is empty */}
            </div>
          );
        },
      },
      {
        id: "lastUpdated",
        accessorFn: (row: any) => formatTimestamp(row.lastUpdated) ?? '',
        header: "Last Updated",
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({cell}: any) => (
          <div>
            <IconButton
              aria-label="edit"
              onClick={() => handleEdit(cell.row.index)}
            >
              <EditIcon/>
            </IconButton>
            <IconButton
              aria-label="delete"
              onClick={() => handleDelete(cell.row.index)}
            >
              <DeleteIcon/>
            </IconButton>
          </div>
        ),
      }];

    const thumbnailCol = {
      id: "isMainTableThumbnail",
      header: "Set as Table Thumbnail",
      Cell: ({row}: { row: MRT_Row<any> }) => {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.checked && props?.isLatestVersion) {
            handleThumbnailSelection(row.index);
            postRWOUpdate({id: props.object.id, index: row.index});
          }
        };
        return (
          <Radio
            checked={row.original.isMainTableThumbnail}
            onChange={handleChange}
            name="thumbnail-select"
          />
        );
      },
    };
    if (props.isLatestVersion) {
      defaultCols.push(thumbnailCol)
    }

    return defaultCols
  }, [props.object, imageUrl, editableCell]);

  const handleDelete = (rowIndex: number) => {
    setDeleteRowIndex(rowIndex);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteRowIndex !== null && deleteRowIndex >= 0 && deleteRowIndex < rowsData.length) {
      const fileNameToDelete = rowsData[deleteRowIndex].fileName;
      setImageKey(fileNameToDelete);
      setIsDeleteMode(true);
      setDeleteDialogOpen(false);

      const formData = new FormData();
      formData.append("isDeleteMode", "true");
      formData.append("imageKey", fileNameToDelete);
      setPendingImages((prevPendingImages) => [...prevPendingImages, formData]);
      setRowsData((prevRowsData) => {
        const newRowsData = [...prevRowsData];
        newRowsData.splice(deleteRowIndex, 1); // Remove the row at deleteRowIndex
        return newRowsData;
      });

    } else {
      console.error("Invalid deleteRowIndex:", deleteRowIndex);
    }
  };

  const handleAddImageClick = () => {
    setIsEditMode(false); // Disable edit mode
    setIsDeleteMode(false); // Disable delete mode
    setFileDialogOpen(true);
  };

  const handleFileDialogClose = () => {
    setFileDialogOpen(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setNewImage(URL.createObjectURL(file));
      setImageFile(file);
      setNewFileName(file.name)
      // The dialog does not close here, it only closes on submit or cancel.
    }
  };

  /**
   * Handles the edit action for a specific row in the table.
   *
   * @param rowIndex - The index of the row to be edited.
   */
  const handleEdit = (rowIndex: number) => {
    setRowsData((prevRowsData) => {
      if (prevRowsData.length === 0) {
        console.error("No rows data available.");
        return prevRowsData;
      }

      if (rowIndex < 0 || rowIndex >= prevRowsData.length) {
        console.error(`Invalid rowIndex: ${rowIndex}`);
        return prevRowsData;
      }

      const rowData = prevRowsData[rowIndex];

      if (rowData && rowData.fileName) {
        // setExistingFileName(rowData.fileName);  // Store the fileName from the row in a state
        setIsEditMode(true);
        setIsDeleteMode(false);
        setImageKey(rowData.image);
        setFileDialogOpen(true);
        setSelectedImageUrl(null); // Ensure the image dialog is closed
        setNewImage(rowData.fileName);
        setEditRowIndex(rowIndex); // Store the rowIndex for later use in handleSubmit

      } else {
        console.error("Row data is undefined or fileName is missing.");
      }

      return [...prevRowsData]; // Return a new array to force re-render
    });
  };


  const handleSubmit = async () => {
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    const file = fileInput?.files?.[0];

    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("id", props?.object?.id);
    formData.append("isEditMode", isEditMode.toString());
    formData.append("newFileName", newFileName)
    formData.append("isDeleteMode", isDeleteMode.toString());
    formData.append("imageKey", imageKey);
    //temporary record to show in table before "Save Version" is clicked including image file that is in memory 
    const newRow = {
      type: "Generic",
      storageLocation: "",
      latestVersion: "1.0",
      lastUpdated: getDateTime(true),
      isMainTableThumbnail: false,
      image: newFileName,
      fileName: newFileName,
      comments: "",
      actions: "",
      tempFile: file
    };
    setRowsData([...rowsData, newRow]);
    // Instead of uploading immediately, save the formData for later
    setPendingImages((prevPendingImages) => [...prevPendingImages, formData]);
    handleFileDialogClose();
  };

  return (
    <div style={{padding: "0px", margin: "0px"}}>
      <Box display="flex" justifyContent="flex-end" sx={{mb: 0, mt: 0, padding: 0}}>
        <IconButton color="primary" aria-label="add" onClick={handleAddImageClick}>
          <AddIcon/>
        </IconButton>
      </Box>
      <ArgosTableSimple
        columns={columns}
        data={rowsData}
        initialState={{density: "compact"}}
        enablePagination={false}
        enableBottomToolbar={false}
        enableHiding={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        // enableTopToolBar should be disabled in order to remove border. removing icons will still have this 
        // whitespace border unless below is explicitly set to false 
        enableTopToolbar={false}
      />
      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Box>Are you sure you want to delete this image (version)?</Box>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
              Close
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              style={{marginLeft: '8px'}}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!selectedImageUrl && !isEditMode} // Only open the image dialog when not in edit mode
        onClose={() => setSelectedImageUrl(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {selectedImageUrl && <img src={selectedImageUrl} alt="Full Size" style={{width: '100%'}}/>}
        </DialogContent>
      </Dialog>
      <Dialog
        open={fileDialogOpen}
        onClose={handleFileDialogClose}
      >
        <DialogTitle>{isEditMode ? "Edit Image" : "Add New Image"}</DialogTitle> {/* Adjusted dialog title based on mode */}
        <DialogContent>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,image/*"
            onChange={handleFileChange}
          />
          <Button onClick={handleSubmit}>Submit</Button>
          <Button onClick={handleFileDialogClose}>Cancel</Button>
        </DialogContent>
      </Dialog>
    </div>
  );

};

export default ImageProperties;

function TabPanel(props: any) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}
